import { formatCents } from "@/lib/money";

import { BarList, ReportSection, Stat, StatGrid } from "../ReportPrimitives";
import type { ExecutiveReport } from "../reportTables";

const monthLabel = (month: string) => {
  const [year, m] = month.split("-");
  return new Date(Number(year), Number(m) - 1, 1).toLocaleDateString(
    undefined,
    {
      month: "short",
      year: "2-digit",
    },
  );
};

/**
 * Executive: the whole business on one screen. Values labelled "current" are
 * point-in-time balances that deliberately ignore the date filter, money owed
 * does not belong to the period it was invoiced in.
 */
export const ExecutivePanel = ({ data }: { data: ExecutiveReport }) => (
  <div className="flex flex-col gap-4">
    <StatGrid>
      <Stat
        label="Revenue"
        value={formatCents(data.revenue_collected_cents)}
        helper="Collected, net of refunds"
        tone="positive"
      />
      <Stat
        label="Invoiced"
        value={formatCents(data.invoiced_cents)}
        helper="Issued in this period"
      />
      <Stat
        label="Outstanding"
        value={formatCents(data.outstanding_cents)}
        helper="Owed right now"
        tone={data.outstanding_cents > 0 ? "negative" : "default"}
      />
      <Stat
        label="Open pipeline"
        value={formatCents(data.open_pipeline_cents)}
        helper="Deals still in play"
      />
    </StatGrid>

    <StatGrid>
      <Stat
        label="New contacts"
        value={String(data.new_contacts)}
        helper="Added in this period"
      />
      <Stat
        label="Deals won"
        value={String(data.deals_won)}
        helper={`${formatCents(data.deals_won_cents)} in value`}
      />
      <Stat
        label="Jobs completed"
        value={String(data.jobs_completed)}
        helper="Finished in this period"
      />
      <Stat
        label="Jobs open"
        value={String(data.jobs_open)}
        helper="Not yet completed"
      />
    </StatGrid>

    <ReportSection
      title="Revenue by month"
      subtitle="Payments received, net of refunds"
    >
      <BarList
        items={data.revenue_by_month.map((row) => ({
          label: monthLabel(row.month),
          value: Math.max(0, row.cents),
          display: formatCents(row.cents),
        }))}
        empty="No payments recorded in this period."
      />
    </ReportSection>
  </div>
);
